import React, {useState, useEffect} from 'react';
import {Select, notification, Button, Input, Switch, Row, Col, DatePicker, InputNumber, Checkbox, Tooltip} from 'antd';
import { ClearOutlined, RedoOutlined } from '@ant-design/icons';
import moment from "moment-timezone";
import http from "../../Utilities/http";
import './ShippingOrderFormConfig.css';
import Heading from "../Common/Heading";
import Wrapper from "../Common/Wrapper";
import EntitySelector from "./EntitySelector";
import ApiService from "../../services/ApiService";

const {Option} = Select;

const shippingOrderFields = [
  'isReceiverDropDown',
  'invoiceNumber', 'docDate', 'dispatchDate', 'receiverName', 'receiverAddress', 'receiverTown', 'receiverCounty',
  'receiverZipcode', 'receiverTel', 'receiverMobile', 'unit', 'measurementUnit', 'uDStringField2', 'boxWeight', 'boxVolume',
  'deliveryDate', 'deliverySlot', 'paymentType', 'paymentAmount', 'paymentCode', 'isDifferentInvoiceReceiver',
  'receiverInvoiceName', 'receiverInvoiceAddress', 'receiverInvoiceTown', 'receiverInvoiceCounty',
  'receiverInvoiceZipcode', 'receiverInvoiceTel', 'receiverInvoiceMobile', 'receiverInvoicePaymentType',
  'receiverInvoicePaymentAmount', 'receiverInvoiceTaxRegistrationNumber', 'receiverInvoiceTaxOffice', 'comments'
];

const fieldDisplayNames = {
  isReceiverDropDown: 'Επιλογή Παραλήπτη Από Υποκαταστήματα',
  invoiceNumber: 'Αριθμός παραστατικού',
  docDate: 'Ημερομηνία καταχώρησης',
  dispatchDate: 'Ημερομηνία Αποστολής',
  receiverName: 'Παραλήπτης',
  receiverAddress: 'Οδός',
  receiverTown: 'Πόλη',
  receiverCounty: 'Νομός',
  receiverZipcode: 'Τ.Κ.',
  receiverTel: 'Τηλέφωνο παραλήπτη',
  receiverMobile: 'Κινητό παραλήπτη',
  unit: 'Ποσότητα',
  measurementUnit: 'Μονάδα μέτρησης',
  uDStringField2: 'Τύπος Δέματος',
  boxWeight: 'Βάρος',
  boxVolume: 'Όγκος',
  deliveryDate: 'Ημερομηνία Παράδοσης',
  deliverySlot: 'Χρονοθυρίδα παράδοσης',
  paymentType: 'Τρόπος πληρωμής εμπορευμάτων',
  paymentAmount: 'Ποσό πληρωμής εμπορευμάτων',
  paymentCode: 'Κωδικός πληρωμής',
  isDifferentInvoiceReceiver: 'Χρέωση παραλήπτη',
  receiverInvoiceName: 'Παραλήπτης τιμολόγησης',
  receiverInvoiceAddress: 'Οδός παραλήπτη τιμολόγησης',
  receiverInvoiceTown: 'Πόλη παραλήπτη τιμολόγησης',
  receiverInvoiceCounty: 'Νομός παραλήпτη τιμολόγησης',
  receiverInvoiceZipcode: 'Τ.Κ. παραλήπτη τιμολόγησης',
  receiverInvoiceTel: 'Τηλέφωνο παραλήпτη τιμολόγησης',
  receiverInvoiceMobile: 'Κινητό παραλήпτη τιμολόγησης',
  receiverInvoicePaymentType: 'Τρόπος πληρωμής μεταφοράς',
  receiverInvoicePaymentAmount: 'Ποσό πληρωμής υπηρεσίας',
  receiverInvoiceTaxRegistrationNumber: 'ΑΦΜ παραλήπτη τιμολόγησης',
  receiverInvoiceTaxOffice: 'ΔΟΥ παραλήπτη τιμολόγησης',
  comments: 'Σχόλια'
};

const fieldTypes = {
  isReceiverDropDown: 'boolean',
  invoiceNumber: 'text',
  docDate: 'date',
  dispatchDate: 'date',
  receiverName: 'text',
  receiverAddress: 'text',
  receiverTown: 'text',
  receiverCounty: 'text',
  receiverZipcode: 'text',
  receiverTel: 'text',
  receiverMobile: 'text',
  unit: 'number',
  measurementUnit: 'select',
  udStringField2: 'select',
  boxWeight: 'number',
  boxVolume: 'number',
  deliveryDate: 'date',
  deliverySlot: 'text',
  paymentType: 'select',
  paymentAmount: 'number',
  paymentCode: 'text',
  isDifferentInvoiceReceiver: 'boolean',
  receiverInvoiceName: 'text',
  receiverInvoiceAddress: 'text',
  receiverInvoiceTown: 'text',
  receiverInvoiceCounty: 'text',
  receiverInvoiceZipcode: 'text',
  receiverInvoiceTel: 'text',
  receiverInvoiceMobile: 'text',
  receiverInvoicePaymentType: 'select',
  receiverInvoicePaymentAmount: 'number',
  receiverInvoiceTaxRegistrationNumber: 'text',
  receiverInvoiceTaxOffice: 'select',
  comments: 'textarea',
};

function ShippingOrderFormConfig() {
  const [queryBy, setQueryBy] = useState('userId');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [config, setConfig] = useState(null);
  const [fieldsConfig, setFieldsConfig] = useState({});
  const [originalFieldsConfig, setOriginalFieldsConfig] = useState({});
  const [taxOffices, setTaxOffices] = useState([]);
  const [measurementUnits, setMeasurementUnits] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    fetchStaticData();
  }, []);

  useEffect(() => {
    if (config && config.fieldsConfig) {
      setFieldsConfig(config.fieldsConfig);
      setOriginalFieldsConfig(config.fieldsConfig);
    } else {
      setFieldsConfig({});
      setOriginalFieldsConfig({});
    }
  }, [config]);

  const fetchStaticData = () => {
    http.get("/taxOffice")
      .then((res) => {
        setTaxOffices(res.data);
      })
      .catch((error) => {
        setTaxOffices([]);
      });

    http.get("/measurementunit")
      .then((res) => {
        setMeasurementUnits(res.data);
      })
      .catch((error) => {
        setMeasurementUnits([]);
      });

    http.get("/shippingorder/payment-methods")
      .then((res) => {
        setPaymentMethods(res.data);
      })
      .catch((error) => {
        setPaymentMethods([]);
      });
  };

  const handleQueryByChange = (value) => {
    setQueryBy(value);
    setSelectedEntity(null);
    setConfig(null);
    setFieldsConfig({});
    if (value === 'default') {
      fetchConfig(null, value);
    }
  };

  const handleEntitySelect = (value) => {
    setSelectedEntity(value);
    fetchConfig(value);
  };

  const fetchConfig = async (entityId, queryByValue) => {
    const qBy = queryByValue || queryBy;
    if (!entityId && qBy !== 'default') return;

    const entityType = qBy === 'default' ? 'global' : qBy.replace('Id', '');
    try {

      const body = await ApiService.getShippingOrderFormConfig(entityType, entityId || '0');
      setConfig(body ?? {prints: {}, fieldsConfig: {}});
    } catch (error) {
      console.error('Error fetching config:', error);
      setConfig({})
    }
  };

  const handleFieldConfigChange = (field, property, value) => {
    setFieldsConfig(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [property]: value
      }
    }));
  };

  const handleConfigureToggle = (field, configured) => {
    const newFieldsConfig = {...fieldsConfig};
    if (configured) {
      const originalFieldConfig = originalFieldsConfig[field];
      if (originalFieldConfig) {
        newFieldsConfig[field] = originalFieldConfig;
      } else {
        newFieldsConfig[field] = {defaultValue: null, locked: false, hidden: false};
      }
    } else {
      delete newFieldsConfig[field];
    }
    setFieldsConfig(newFieldsConfig);
  };

  const handleSaveConfig = async () => {
    const isDefault = queryBy === 'default';

    let payload;
    if (isDefault) {
      payload = {
        level: 'global',
        target_id: 0,
        config: {
          ...config,
          fieldsConfig: fieldsConfig
        }
      };
    } else {
      payload = {
        target: queryBy.replace('Id', ''),
        id: selectedEntity,
        config: {
          ...config,
          fieldsConfig: fieldsConfig
        }
      };
    }
    const entityType = isDefault ? 'global' : queryBy.replace('Id', '');


    try {
      const response = await ApiService.updateShippingOrderFormConfig(entityType, selectedEntity ?? '0', {
        ...config,
        fieldsConfig: fieldsConfig
      });
      if (response.status !== 200) {
        const errorBody = await response.text();
        throw new Error(errorBody || 'Failed to save configuration.');
      }

      notification.success({
        message: 'Success',
        description: 'Επιτυχής Αποθήκευση.',
      });
      setOriginalFieldsConfig(fieldsConfig);
    } catch (error) {
      console.error('Error saving config:', error);
      notification.error({
        message: 'Error',
        description: error.message,
      });
    }
  };

  const handleCheckAll = (property, checked) => {
    const newFieldsConfig = {...fieldsConfig};
    if (queryBy === 'default') {
      shippingOrderFields.forEach(field => {
        newFieldsConfig[field] = {
          ...(newFieldsConfig[field] || {defaultValue: null, locked: false, hidden: false}),
          [property]: checked
        };
      });
    } else { // queryBy is 'userId' or 'groupId'
      Object.keys(newFieldsConfig).forEach(field => {
        newFieldsConfig[field] = {
          ...newFieldsConfig[field],
          [property]: checked
        };
      });
    }
    setFieldsConfig(newFieldsConfig);
  };

  const handleConfigureAll = (checked) => {
    if (checked) {
      const allFieldsConfigured = {};
      shippingOrderFields.forEach(field => {
        allFieldsConfigured[field] = originalFieldsConfig[field] || {defaultValue: null, locked: false, hidden: false};
      });
      setFieldsConfig(allFieldsConfigured);
    } else {
      setFieldsConfig({});
    }
  };

  const handleResetValues = (e) => {
    const { checked } = e.target;
    setFieldsConfig(currentFieldsConfig => {
      const newFieldsConfig = { ...currentFieldsConfig };
      const fieldsToUpdate = queryBy === 'default'
        ? shippingOrderFields
        : Object.keys(currentFieldsConfig);

      fieldsToUpdate.forEach(field => {
        const fieldConfig = currentFieldsConfig[field] || {};
        if (checked) {
          newFieldsConfig[field] = { ...fieldConfig, defaultValue: null };
        } else {
          newFieldsConfig[field] = { ...fieldConfig, defaultValue: originalFieldsConfig[field]?.defaultValue ?? null };
        }
      });
      return newFieldsConfig;
    });
  };

  const clearAll = () => {
    handleClearAllDefaultValues('clear');
  }

  const resetAll = () => {
    handleClearAllDefaultValues('reset');
  }

  const handleClearAllDefaultValues = (type) => {
    const clear = type === 'clear';
    setFieldsConfig(currentFieldsConfig => {
      const newFieldsConfig = { ...currentFieldsConfig };
      const fieldsToUpdate = queryBy === 'default'
        ? shippingOrderFields
        : Object.keys(currentFieldsConfig);

      fieldsToUpdate.forEach(field => {
        const fieldConfig = currentFieldsConfig[field] || {};
        if (clear) {
          newFieldsConfig[field] = { ...fieldConfig, defaultValue: null };
        } else {
          newFieldsConfig[field] = { ...fieldConfig, defaultValue: originalFieldsConfig[field]?.defaultValue ?? null };
        }
      });
      return newFieldsConfig;
    });
  };

  const renderFieldInput = (field) => {
    const type = fieldTypes[field];
    const value = fieldsConfig[field]?.defaultValue;

    switch (type) {
      case 'date':
        const isToday = value === 'TODAY';
        const isTomorrow = value === 'TOMORROW';
        return (
          <Row align="middle">
            <Col span={10}>
              <DatePicker
                value={isToday || isTomorrow || !value ? null : moment(value)}
                onChange={date => handleFieldConfigChange(field, 'defaultValue', date ? date.toISOString() : null)}
                style={{width: '100%'}}
                disabled={isToday || isTomorrow}
              />
            </Col>
            <Col span={7} style={{display: 'flex', justifyContent: 'center'}}>
              <Checkbox
                checked={isToday}
                onChange={e => {
                  if (e.target.checked) {
                    handleFieldConfigChange(field, 'defaultValue', 'TODAY');
                  } else {
                    handleFieldConfigChange(field, 'defaultValue', null);
                  }
                }}
              >
                Σήμερα
              </Checkbox>
            </Col>
            <Col span={7} style={{display: 'flex', justifyContent: 'center'}}>
              <Checkbox
                checked={isTomorrow}
                onChange={e => {
                  if (e.target.checked) {
                    handleFieldConfigChange(field, 'defaultValue', 'TOMORROW');
                  } else {
                    handleFieldConfigChange(field, 'defaultValue', null);
                  }
                }}
              >
                Αύριο
              </Checkbox>
            </Col>
          </Row>
        );
      case 'number':
        return (
          <InputNumber
            value={value}
            onChange={val => handleFieldConfigChange(field, 'defaultValue', val)}
            style={{width: '100%'}}
          />
        );
      case 'boolean':
        return (
          <Checkbox
            checked={!!value}
            onChange={e => handleFieldConfigChange(field, 'defaultValue', e.target.checked)}
          />
        );
      case 'select':
        let options = [];
        if (field === 'measurementUnit') {
          options = measurementUnits.filter(m => config['allowedMeasurementUnits'] ? config['allowedMeasurementUnits'].find(am => am === m.code) : true)
            .map(unit => ({value: unit.code, label: unit.description}));
        }
        if (field === 'uDStringField2') {
          options = [];
        }
        if (field === 'paymentType') {
          options = [
            {value: 0, label: 'Αντικαταβολή'},
            {value: 1, label: 'Τράπεζα'},
            {value: 2, label: 'Επιταγή'},
            {value: 3, label: 'Μετρητά'},
            {value: 4, label: 'Paypal'},
            {value: 5, label: 'Πιστωτική'},
          ];
        }
        if (field === 'receiverInvoicePaymentType') {
          options = paymentMethods.map(method => ({value: method.description, label: method.description}));
        }
        if (field === 'receiverInvoiceTaxOffice') {
          options = taxOffices.map(office => ({value: office.code, label: office.description}));
        }
        return (
          <Select
            value={value}
            onChange={val => handleFieldConfigChange(field, 'defaultValue', val)}
            style={{width: '100%'}}
            showSearch
            allowClear
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {options.map(opt => <Option key={opt.value} value={opt.value}>{opt.label}</Option>)}
          </Select>
        );
      case 'textarea':
        return (
          <Input.TextArea
            value={value}
            onChange={e => handleFieldConfigChange(field, 'defaultValue', e.target.value)}
          />
        );
      default:
        return (
          <Input
            value={value || ''}
            onChange={e => handleFieldConfigChange(field, 'defaultValue', e.target.value)}
          />
        );
    }
  };

  return (
    <div>
      <Heading>Φόρμα Εντολών Μεταφοράς</Heading>
      <EntitySelector
        onEntitySelect={handleEntitySelect}
        onQueryByChange={handleQueryByChange}
        queryBy={queryBy}
        selectedEntity={selectedEntity}
      />
      {config &&
        <Wrapper>

          <h2>Ρύθμιση Πεδίων</h2>
          {queryBy !== 'default' && (
            <div className="config-display">
              <Row gutter={[16, 16]} style={{marginBottom: '16px'}}>
                <Col span={6}><strong>Field</strong></Col>
                <Col span={4} style={{textAlign: 'center'}}>
                  <strong>Configure</strong>
                  <Checkbox onChange={e => handleConfigureAll(e.target.checked)} style={{marginLeft: '8px'}}/>
                </Col>
                <Col span={8}>
                  <strong>Default Value</strong>
                  <Tooltip title="Clear All">
                    <Button
                      icon={<ClearOutlined />}
                      onClick={clearAll}
                      style={{ marginLeft: '10px' }}
                    />
                  </Tooltip>
                  <Tooltip title="Reset All">
                    <Button
                      icon={<RedoOutlined />}
                      onClick={resetAll}
                      style={{ marginLeft: '10px' }}
                    />
                  </Tooltip>
                </Col>
                <Col span={3} style={{textAlign: 'center'}}>
                  <strong>Locked</strong>
                  <Checkbox onChange={e => handleCheckAll('locked', e.target.checked)} style={{marginLeft: '8px'}}/>
                </Col>
                <Col span={3} style={{textAlign: 'center'}}>
                  <strong>Hidden</strong>
                  <Checkbox onChange={e => handleCheckAll('hidden', e.target.checked)} style={{marginLeft: '8px'}}/>
                </Col>
              </Row>
              {shippingOrderFields.map(field => {
                const isConfigured = fieldsConfig[field] !== undefined;
                return (
                  <Row key={field} gutter={[16, 16]} style={{marginBottom: '8px'}}>
                    <Col span={6} style={{textAlign: 'left'}}>{fieldDisplayNames[field]}</Col>
                    <Col span={4} style={{display: 'flex', justifyContent: 'center'}}>
                      <Switch
                        checked={isConfigured}
                        onChange={checked => handleConfigureToggle(field, checked)}
                      />
                    </Col>
                    <Col span={8}>
                      {isConfigured && renderFieldInput(field)}
                    </Col>
                    <Col span={3} style={{display: 'flex', justifyContent: 'center'}}>
                      {isConfigured && (
                        <Tooltip title="Κλειδωμένο">
                          <span>
                        <Checkbox
                          checked={fieldsConfig[field]?.locked || false}
                          onChange={e => handleFieldConfigChange(field, 'locked', e.target.checked)}
                        />
                          </span>
                        </Tooltip>
                      )}
                    </Col>
                    <Col span={3} style={{display: 'flex', justifyContent: 'center'}}>
                      {isConfigured && (
                        <Tooltip title="Κρυμμένο">
                          <span>
                        <Checkbox
                          checked={fieldsConfig[field]?.hidden || false}
                          onChange={e => handleFieldConfigChange(field, 'hidden', e.target.checked)}
                        />
                          </span>
                        </Tooltip>
                      )}
                    </Col>
                  </Row>
                );
              })}
              <Button
                type="primary"
                onClick={handleSaveConfig}
                style={{marginTop: '10px'}}
              >
                Save
              </Button>
            </div>
          )}
          {queryBy === 'default' && (
            <div className="config-display">
              <Row gutter={[16, 16]} style={{marginBottom: '16px'}}>
                <Col span={8}><strong>Field</strong></Col>
                <Col span={8}>
                  <strong>Default Value</strong>
                  <Tooltip title="Clear All">
                    <Button
                      icon={<ClearOutlined />}
                      onClick={clearAll}
                      style={{ marginLeft: '10px' }}
                    />
                  </Tooltip>
                  <Tooltip title="Reset All">
                    <Button
                      icon={<RedoOutlined />}
                      onClick={resetAll}
                      style={{ marginLeft: '10px' }}
                    />
                  </Tooltip>
                </Col>
                <Col span={4} style={{textAlign: 'center'}}>
                  <strong>Locked</strong>
                  <Checkbox onChange={e => handleCheckAll('locked', e.target.checked)} style={{marginLeft: '8px'}}/>
                </Col>
                <Col span={4} style={{textAlign: 'center'}}>
                  <strong>Hidden</strong>
                  <Checkbox onChange={e => handleCheckAll('hidden', e.target.checked)} style={{marginLeft: '8px'}}/>
                </Col>
              </Row>
              {shippingOrderFields.map(field => {
                const isConfigured = fieldsConfig[field] !== undefined;
                return (
                  <Row key={field} gutter={[16, 16]} style={{marginBottom: '8px'}}>
                    <Col span={8} style={{textAlign: 'left'}}>{fieldDisplayNames[field]}</Col>
                    <Col span={8}>
                      {renderFieldInput(field)}
                    </Col>
                    <Col span={4} style={{display: 'flex', justifyContent: 'center'}}>
                      {(
                        <Tooltip title="Κλειδωμένο">
                          <span>
                        <Checkbox
                          checked={fieldsConfig[field]?.locked || false}
                          onChange={e => handleFieldConfigChange(field, 'locked', e.target.checked)}
                        />
                            </span>
                        </Tooltip>
                      )}
                    </Col>
                    <Col span={4} style={{display: 'flex', justifyContent: 'center'}}>
                      {(
                        <Tooltip title="Κρυμμένο">
                          <span>
                        <Checkbox
                          checked={fieldsConfig[field]?.hidden || false}
                          onChange={e => handleFieldConfigChange(field, 'hidden', e.target.checked)}
                        />
                            </span>
                        </Tooltip>
                      )}
                    </Col>
                  </Row>
                );
              })}
              <Button
                type="primary"
                onClick={handleSaveConfig}
                style={{marginTop: '10px'}}
              >
                Save
              </Button>
            </div>
          )}
        </Wrapper>
      }
    </div>
  )
}

export default ShippingOrderFormConfig;
